import { useIntl } from 'react-intl';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  buildSaveDynamicOptionParams,
  type DynamicFieldOptionParamsType,
} from './useCreateDynamicFieldOption';
import { API } from 'src/reactQuery/Api';
import { putRequest } from 'src/utils/request';
import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';
import messages from 'src/pages/DynamicFieldsPage/messages';
import { RequestSimplifiedData } from 'src/types/requestData';
import { useMessageHandler } from 'src/utils/hooks/useMessageHandler';

type UpdateDynamicFieldOptionParamsType = DynamicFieldOptionParamsType & {
  optionUuid: string;
};

const updateDynamicFieldOption = async (
  dynamicFieldOption: UpdateDynamicFieldOptionParamsType,
) => {
  const { dynamicFieldUuid, optionUuid, ...others } = dynamicFieldOption;

  const { data } = await putRequest<RequestSimplifiedData>(
    API.PUT.updateDynamicFieldOption(dynamicFieldUuid, optionUuid),
    buildSaveDynamicOptionParams({
      dynamicFieldUuid,
      ...others,
    }),
  );

  return data;
};

export const useUpdateDynamicFieldOption = () => {
  const client = useQueryClient();
  const { formatMessage } = useIntl();
  const { handleError, handleSuccess } = useMessageHandler();

  return useMutation({
    mutationFn: updateDynamicFieldOption,
    onSuccess: () => {
      handleSuccess(formatMessage(messages.dynamicOptionSaved), 1000);
      client.invalidateQueries({ queryKey: [QUERY_KEYS.DYNAMIC_FIELDS] });
    },
    onError: handleError,
  });
};
